import { useCallback, useEffect, useRef, useState } from 'react';
import { useConsensusStatus } from '../lib/consensus-status';

interface Props {
  apiUrl: string;
  indexerUrl: string;
  consensusUrl: string;
  onApiUrlChange: (url: string) => void;
  onIndexerUrlChange: (url: string) => void;
  onConsensusUrlChange: (url: string) => void;
}

type HealthState =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'ok'; apiMs: number; indexerMs: number | null }
  | { kind: 'error'; message: string };

async function ping(url: string, path: string): Promise<number> {
  const started = performance.now();
  const res = await fetch(`${url}${path}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return Math.round(performance.now() - started);
}

export function ConnectionBar({
  apiUrl,
  indexerUrl,
  consensusUrl,
  onApiUrlChange,
  onIndexerUrlChange,
  onConsensusUrlChange,
}: Props) {
  const [apiDraft, setApiDraft] = useState(apiUrl);
  const [indexerDraft, setIndexerDraft] = useState(indexerUrl);
  const [consensusDraft, setConsensusDraft] = useState(consensusUrl);
  const [health, setHealth] = useState<HealthState>({ kind: 'idle' });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  // Bumped on every check; in-flight checks compare to ignore stale results
  // when URLs change mid-check.
  const genRef = useRef(0);
  // Consensus /status fetch is owned by the shared ConsensusStatusProvider so
  // HomePanel and ConnectionBar don't double-fetch on page load.
  const { latencyMs: consensusMs, refresh: refreshConsensus } = useConsensusStatus();

  useEffect(() => setApiDraft(apiUrl), [apiUrl]);
  useEffect(() => setIndexerDraft(indexerUrl), [indexerUrl]);
  useEffect(() => setConsensusDraft(consensusUrl), [consensusUrl]);

  // Close the popover on outside click — same pattern ExamplesDropdown uses.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const check = useCallback(async (silent = false) => {
    const myGen = ++genRef.current;
    if (!silent) setHealth({ kind: 'checking' });
    try {
      const apiMs = await ping(apiUrl, '/state/root-hash');
      let indexerMs: number | null = null;
      if (indexerUrl) {
        try {
          indexerMs = await ping(indexerUrl, '/health');
        } catch {
          // Indexer health endpoint may not exist — try a different path
          try {
            indexerMs = await ping(indexerUrl, '/');
          } catch {
            indexerMs = null;
          }
        }
      }
      if (genRef.current === myGen) {
        setHealth({ kind: 'ok', apiMs, indexerMs });
      }
    } catch (err) {
      if (genRef.current === myGen) {
        setHealth({
          kind: 'error',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    }
  }, [apiUrl, indexerUrl]);

  const recheckAll = useCallback(() => {
    check(false);
    refreshConsensus();
  }, [check, refreshConsensus]);

  // Auto-detect connection: vocal initial check on mount/URL-change, silent
  // 30s polls + on-visibility re-check so the dot reflects live state without
  // the user clicking. `silent=true` skips the "checking" flash so the dot
  // doesn't flicker grey every 30s when nothing's actually changed.
  useEffect(() => {
    let cancelled = false;
    check(false);
    const interval = setInterval(() => {
      if (!cancelled && !document.hidden) check(true);
    }, 30_000);
    const onVisibility = () => {
      if (!cancelled && !document.hidden) check(true);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [check]);

  // Status indicator on the toggle button — green when last check succeeded,
  // red on failure, neutral otherwise. Conveys connection state without
  // taking up header real estate for the URL fields.
  const statusDot =
    health.kind === 'ok' ? 'ok'
    : health.kind === 'error' ? 'err'
    : 'idle';

  const statusLabel =
    health.kind === 'ok' ? `connected · ${health.apiMs}ms`
    : health.kind === 'error' ? 'offline'
    : 'connection';

  const buttonText =
    health.kind === 'ok' ? 'live'
    : health.kind === 'error' ? 'offline'
    : 'connecting…';

  return (
    <div className="connection-bar" ref={ref}>
      <button
        className="btn btn-ghost connection-toggle"
        onClick={() => setOpen(!open)}
        title={statusLabel}
      >
        <span className={`status-dot status-${statusDot}`} aria-hidden="true" />
        <span className="connection-toggle-label">{buttonText}</span>
      </button>
      {open && (
        <div className="connection-menu">
          <label className="connection-label">
            Validator
            <input
              className="connection-input"
              value={apiDraft}
              onChange={(e) => setApiDraft(e.target.value)}
              onBlur={() => onApiUrlChange(apiDraft)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onApiUrlChange(apiDraft);
              }}
              spellCheck={false}
            />
          </label>
          <label className="connection-label">
            Indexer
            <input
              className="connection-input"
              value={indexerDraft}
              onChange={(e) => setIndexerDraft(e.target.value)}
              onBlur={() => onIndexerUrlChange(indexerDraft)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onIndexerUrlChange(indexerDraft);
              }}
              placeholder="(optional — auto-discovered if blank)"
              spellCheck={false}
            />
          </label>
          <label className="connection-label">
            Consensus
            <input
              className="connection-input"
              value={consensusDraft}
              onChange={(e) => setConsensusDraft(e.target.value)}
              onBlur={() => onConsensusUrlChange(consensusDraft)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onConsensusUrlChange(consensusDraft);
              }}
              placeholder="(optional — auto-derived from validator port)"
              spellCheck={false}
            />
          </label>
          <div className="connection-actions">
            <button
              className="btn"
              onClick={recheckAll}
              disabled={health.kind === 'checking'}
            >
              {health.kind === 'checking' ? 'Checking…' : 'Re-check'}
            </button>
            <div className="connection-status">
              {health.kind === 'idle' && <span className="status-idle">not checked</span>}
              {health.kind === 'ok' && (
                <span className="status-ok">
                  ● validator {health.apiMs}ms
                  {consensusMs !== null && ` · consensus ${consensusMs}ms`}
                  {health.indexerMs !== null && ` · indexer ${health.indexerMs}ms`}
                </span>
              )}
              {health.kind === 'error' && (
                <span className="status-err" title={health.message}>
                  ● offline — {health.message}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
